/**
 * Раздел «Настройки»: учётные записи и матрица выдачи «роль × раздел»
 * (карточка BL-2026-10-08-5204).
 *
 * Flow: раздел смонтирован → GET /api/users отдаёт записи вместе с
 *       ВЫЧИСЛЕННЫМИ разделами каждой → строка разворачивается в правку
 *       (PATCH реквизитов и роли, POST пароля, activate/deactivate) →
 *       ниже GET /api/access/sections отдаёт каталог разделов и матрицу,
 *       правка матрицы уходит одним PUT целиком → после сохранения матрицы
 *       список записей перечитывается: разделы у них вычисляются из роли,
 *       и правка матрицы меняет их тут же, без выкладки.
 *
 * ЧТО ЭТОТ ЭКРАН ЗАМЕНЯЕТ. Прежде в «Сводке» стояла строка «Учётных
 * записей: N. Управление — через API /api/users»: интерфейс честно
 * сообщал, что управления в нём нет, а роли и доступы правились запросом
 * из терминала — то есть настройкой, которую нельзя поручить. ФИО записи
 * Inav_Deal пришлось проставлять прямым UPDATE по базе (техспека §2).
 *
 * ДВА ОПАСНЫХ МЕСТА, из-за которых этот файл такой осторожный:
 *
 *   1. Пароль показывается ОДИН раз. Хранить его негде, отправить человеку
 *      нечем — почтового контура у системы нет. Пропавший с экрана пароль
 *      не восстановить: его придётся менять заново. Поэтому блок с
 *      паролем не закрывается ни перерисовкой, ни щелчком мимо, ни
 *      вторым действием — только явным подтверждением, что он сохранён
 *      (см. PasswordOnce).
 *   2. PUT матрицы заменяет выдачу ЦЕЛИКОМ. Роль, отсутствующая в теле,
 *      теряет все разделы. При живой проверке предыдущей карточки
 *      частичный PUT обнулил выдачу трёх ролей, и заметно это стало
 *      только по следующему запросу. Поэтому экран всегда собирает полную
 *      матрицу и называет роли, которые останутся пустыми, ДО сохранения
 *      (см. AccessMatrixBlock).
 *
 * Зависимости:
 *   - react: состояние, жизненный цикл, ссылка на поле пароля;
 *   - ./api: listUsers, createUser, updateUser, setUserPassword,
 *     setUserActive, loadAccessSections, saveAccessSections;
 *   - ./roles: перечень и названия ролей (набор закрыт и живёт в коде).
 *
 * Связанные файлы:
 *   - src/App.jsx — реестр SECTION_VIEWS, код раздела `settings`;
 *   - src/SettingsPanel.test.jsx — проверки приёмки §6;
 *   - docs/specs/BL-2026-10-08-5204-settings-users.md — §4 контракт,
 *     §5 экран, §6 приёмка;
 *   - docs/specs/BL-2026-10-08-a315-section-access.md — §3.3 неотзывность
 *     выдачи admin, §5 форма матрицы.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import {
  createUser,
  listUsers,
  loadAccessSections,
  saveAccessSections,
  setUserActive,
  setUserPassword,
  updateUser,
} from "./api";
import { ROLE_CODES, roleTitle } from "./roles";

/**
 * Показывает момент входа в UTC.
 *
 * @param {string|null} value — ISO-момент из ответа сервера
 * @returns {string} «YYYY-MM-DD HH:MM UTC», либо «ни разу» для пустого
 *          значения, либо само значение, если его не разобрать
 *
 * «Ни разу», а не прочерк: отсутствие последнего входа — это факт о записи
 * (её завели, но ею не пользовались), а не пробел в данных. UTC подписан
 * явно: всё хранение и сравнение в проекте идёт в UTC, и момент без
 * пометки читался бы как местное время.
 */
export function formatLastLogin(value) {
  if (!value) return "ни разу";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

/**
 * Собирает ПОЛНУЮ матрицу для отправки в PUT /api/access/sections.
 *
 * @param {Array<{role: string, locked: boolean}>} roles — строки матрицы из
 *        ответа сервера (все роли модели, включая пустые и locked)
 * @param {object} draft — черновик правки: код роли → массив кодов разделов
 * @param {Array<{code: string}>} catalog — каталог разделов в порядке сервера
 * @returns {Array<{role: string, sections: Array<string>}>} тело запроса
 *
 * Включаются ВСЕ правимые роли, а не изменённые: PUT заменяет выдачу
 * целиком, и роль, не попавшая в тело, теряет все разделы. Именно так при
 * живой проверке предыдущей карточки обнулилась выдача трёх ролей.
 *
 * Строка locked (это admin) исключается: её выдача не хранится и
 * неотзываема, и сервер отвергает тело, в котором она есть, — с названной
 * причиной (техспека BL-2026-10-08-a315 §3.3).
 *
 * Порядок кодов внутри роли — КАТАЛОЖНЫЙ, а не порядок щелчков: тело
 * запроса сравнивают глазами в журнале изменений, и перестановка кодов
 * выглядела бы правкой там, где ничего не менялось.
 */
export function fullMatrixPayload(roles, draft, catalog) {
  const порядок = catalog.map((s) => s.code);
  return roles
    .filter((r) => !r.locked)
    .map((r) => ({
      role: r.role,
      sections: порядок.filter((code) => (draft[r.role] || []).includes(code)),
    }));
}

/**
 * Роли, у которых в черновике не осталось ни одного раздела.
 *
 * @param {Array<{role: string, sections: Array<string>}>} payload — тело,
 *        которое уйдёт в PUT (результат fullMatrixPayload)
 * @returns {Array<string>} коды таких ролей в порядке матрицы
 *
 * Отдельная функция, а не условие внутри разметки: это и есть то
 * предупреждение, которое обязано прозвучать ДО сохранения (приёмка §6
 * п. 6), и проверять его надо без отрисовки экрана.
 */
export function rolesLeftEmpty(payload) {
  return payload.filter((r) => r.sections.length === 0).map((r) => r.role);
}

/**
 * Пароль, показанный ОДИН раз.
 *
 * @param {object} shown — { username, password, kind } — чья запись, пароль
 *        и повод: 'created' (запись заведена) либо 'reset' (пароль сменён)
 * @param {function} onDismiss — убрать блок; вызывается только после
 *        явного подтверждения
 *
 * ПОЧЕМУ ТАК СЛОЖНО ДЛЯ ОДНОЙ СТРОКИ ТЕКСТА. Пароль существует только
 * здесь: сервер отдал ответ без него, в списке записей его нет, хранить
 * его негде, а отправить человеку нечем — почтового контура у системы нет
 * (техспека §5.1 и §8). Исчезнув с экрана, он не восстанавливается: пароль
 * придётся менять заново, и человек в это время не войдёт. Отсюда три
 * решения:
 *
 *   - пароль лежит в ПОЛЕ ввода, а не в абзаце: его можно выделить мышью,
 *     скопировать кнопкой и прочитать посимвольно; поле только для чтения,
 *     чтобы правка не выдала за пароль то, чего сервер не знает;
 *   - кнопка «Скопировать» кладёт пароль в буфер обмена, а там, где
 *     браузер буфер не даёт (нет разрешения, не защищённое соединение),
 *     ВЫДЕЛЯЕТ его и говорит, что нажать, — молчаливого «ничего не
 *     произошло» здесь быть не должно;
 *   - закрывается блок только отметкой «пароль сохранён» и нажатием
 *     «Скрыть». Ни щелчок мимо, ни перерисовка списка, ни таймер его не
 *     убирают. Таймера нет намеренно: исчезнувший сам собой пароль — это
 *     ровно тот случай, который нечем починить.
 */
function PasswordOnce({ shown, onDismiss }) {
  const [saved, setSaved] = useState(false);
  const [hint, setHint] = useState("");
  const field = useRef(null);

  /**
   * Кладёт пароль в буфер обмена, а если нельзя — выделяет его.
   *
   * Побочные эффекты: запись в буфер обмена браузера, выделение текста в
   * поле, подсказка под полем.
   */
  function copy() {
    if (field.current) {
      field.current.focus();
      field.current.select();
    }
    const буфер =
      typeof navigator !== "undefined" && navigator.clipboard
        ? navigator.clipboard
        : null;
    if (буфер && typeof буфер.writeText === "function") {
      буфер
        .writeText(shown.password)
        .then(() => setHint("Пароль скопирован в буфер обмена."))
        .catch(() =>
          setHint("Буфер обмена недоступен. Пароль выделен — нажмите Ctrl+C."),
        );
      return;
    }
    setHint("Буфер обмена недоступен. Пароль выделен — нажмите Ctrl+C.");
  }

  return (
    <div className="password-once">
      <h3>
        {shown.kind === "created"
          ? `Запись «${shown.username}» заведена`
          : `Пароль записи «${shown.username}» изменён`}
      </h3>
      <p className="warn">
        Пароль показан один раз — больше не покажем. Система его не хранит в
        открытом виде и переслать его человеку ей нечем. Передайте пароль лично;
        если он потерян, остаётся только задать новый.
      </p>
      <div className="inline">
        <input
          ref={field}
          type="text"
          readOnly
          value={shown.password}
          aria-label="Новый пароль"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" onClick={copy}>
          Скопировать
        </button>
      </div>
      {hint && <p className="muted">{hint}</p>}
      <label className="check">
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        <span>Пароль сохранён или передан</span>
      </label>
      <div className="inline">
        <button type="button" disabled={!saved} onClick={onDismiss}>
          Скрыть
        </button>
      </div>
    </div>
  );
}

/**
 * Разделы, доступные учётной записи, в ячейке таблицы.
 *
 * @param {object} row — запись из GET /api/users: поля sections и
 *        sections_locked
 *
 * Разделы ВЫЧИСЛЕНЫ сервером из роли через матрицу выдачи, а не хранятся
 * при записи (техспека §3.3): хранение завело бы второй источник истины,
 * который разошёлся бы с матрицей при первой же её правке.
 *
 * sections_locked (он приходит у admin) показывается словом «неотзываемо»,
 * а не замочком: человек должен понимать, что перечень нельзя сократить, —
 * и почему. Право администратора на все разделы зашито в коде именно для
 * того, чтобы запереть систему изнутри было невозможно.
 */
function SectionsCell({ row }) {
  const sections = Array.isArray(row.sections) ? row.sections : [];

  if (sections.length === 0) {
    return (
      <span className="muted">
        не выдано ни одного — человек увидит экран-объяснение
      </span>
    );
  }

  return (
    <>
      {sections.map((s) => s.title).join(", ")}
      {row.sections_locked && (
        <span
          className="badge"
          title="Все разделы, и перечень нельзя сократить: право администратора на них зашито в коде. Иначе администратор, снявший себе последний раздел, терял бы и доступ к экрану, на котором это чинится."
        >
          неотзываемо
        </span>
      )}
    </>
  );
}

/**
 * Правка одной учётной записи — раскрытая строка таблицы.
 *
 * @param {object} row — запись из GET /api/users
 * @param {boolean} isSelf — это запись текущего пользователя
 * @param {boolean} blocked — показан несохранённый пароль, и выдавать
 *        второй нельзя
 * @param {function} onPatch — ({full_name, position, department, role}) →
 *        Promise; PATCH записи
 * @param {function} onPassword — (пароль) → Promise; POST нового пароля
 * @param {function} onActive — (boolean) → Promise; включение/отключение
 *
 * Правка — РАСКРЫТАЯ строка, а не отдельный экран: администратор правит
 * одну запись, а смотрит на перечень целиком, и уходить с перечня ради
 * смены отдела означало бы терять место, куда он смотрел.
 *
 * Кнопка отключения СВОЕЙ записи не блокируется здесь намеренно. Запрет
 * держит сервер и называет причину («нельзя отключить самого себя»);
 * вторая проверка в JSX была бы вторым местом решения — тем самым
 * дефектом, от которого избавлялась предыдущая карточка, — и разошлась бы
 * с сервером молча. В строке стоит пометка «это вы», чтобы нажатие не
 * было неожиданностью.
 */
function UserEditor({ row, isSelf, blocked, onPatch, onPassword, onActive }) {
  const [draft, setDraft] = useState({
    full_name: row.full_name || "",
    position: row.position || "",
    department: row.department || "",
    role: row.role,
  });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  /**
   * Выполняет действие, пока оно идёт — держит кнопки отключёнными.
   *
   * Повторное нажатие на «Сохранить» в ожидании ответа отправило бы вторую
   * правку той же записи; для смены пароля это означало бы ДВА пароля,
   * из которых показан один.
   */
  const run = (действие) => {
    setBusy(true);
    Promise.resolve(действие()).finally(() => setBusy(false));
  };

  return (
    <div className="user-editor">
      <div className="user-editor-fields">
        <label className="filter">
          <span>ФИО</span>
          <input
            type="text"
            value={draft.full_name}
            onChange={set("full_name")}
          />
        </label>
        <label className="filter">
          <span>Должность</span>
          <input
            type="text"
            value={draft.position}
            onChange={set("position")}
          />
        </label>
        <label className="filter">
          <span>Отдел</span>
          <input
            type="text"
            value={draft.department}
            onChange={set("department")}
          />
        </label>
        <label className="filter">
          <span>Роль</span>
          <select value={draft.role} onChange={set("role")}>
            {ROLE_CODES.map((code) => (
              <option key={code} value={code}>
                {roleTitle(code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="inline">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onPatch(draft))}
        >
          Сохранить
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onActive(!row.is_active))}
        >
          {row.is_active ? "Отключить" : "Включить"}
        </button>
        {isSelf && <span className="muted">это вы</span>}
      </div>

      <h4>Смена пароля</h4>
      {blocked ? (
        <p className="muted">
          Сначала сохраните показанный выше пароль: второй пароль заменил бы его
          на экране, а восстановить первый нечем.
        </p>
      ) : (
        <div className="inline">
          <input
            type="text"
            value={password}
            placeholder="новый пароль"
            aria-label="Новый пароль для записи"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            disabled={busy || password.trim() === ""}
            onClick={() =>
              run(() => onPassword(password).then(() => setPassword("")))
            }
          >
            Сменить пароль
          </button>
        </div>
      )}
      <p className="muted">
        Пароль будет показан один раз — сразу после смены, и больше не покажем.
      </p>
    </div>
  );
}

/**
 * Форма заведения учётной записи.
 *
 * @param {function} onCreate — (поля) → Promise; POST /api/users
 * @param {function} onCancel — закрыть форму
 *
 * Должность и отдел необязательны: у записей, заведённых до миграции 053,
 * их нет, и требовать их теперь значило бы объявить существующие записи
 * неполными. Логин и пароль обязательны — без них запись бессмысленна;
 * длину проверяет сервер, и его причина показывается текстом, а не
 * переписывается здесь второй копией правила.
 */
function CreateUserForm({ onCreate, onCancel }) {
  const [draft, setDraft] = useState({
    username: "",
    full_name: "",
    position: "",
    department: "",
    role: "viewer",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  function submit(event) {
    event.preventDefault();
    setBusy(true);
    Promise.resolve(onCreate(draft)).finally(() => setBusy(false));
  }

  return (
    <form className="user-create" onSubmit={submit}>
      <div className="user-editor-fields">
        <label className="filter">
          <span>Логин</span>
          <input
            type="text"
            value={draft.username}
            autoComplete="off"
            onChange={set("username")}
          />
        </label>
        <label className="filter">
          <span>ФИО</span>
          <input
            type="text"
            value={draft.full_name}
            onChange={set("full_name")}
          />
        </label>
        <label className="filter">
          <span>Должность</span>
          <input
            type="text"
            value={draft.position}
            onChange={set("position")}
          />
        </label>
        <label className="filter">
          <span>Отдел</span>
          <input
            type="text"
            value={draft.department}
            onChange={set("department")}
          />
        </label>
        <label className="filter">
          <span>Роль</span>
          <select value={draft.role} onChange={set("role")}>
            {ROLE_CODES.map((code) => (
              <option key={code} value={code}>
                {roleTitle(code)}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          <span>Пароль</span>
          <input
            type="text"
            value={draft.password}
            autoComplete="off"
            onChange={set("password")}
          />
        </label>
      </div>
      <p className="muted">
        Пароль показывается один раз — сразу после заведения. Система его не
        хранит в открытом виде и переслать человеку не может: передайте лично.
      </p>
      <div className="inline">
        <button
          type="submit"
          disabled={busy || !draft.username || !draft.password}
        >
          {busy ? "Заводим…" : "Завести"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

/**
 * Блок учётных записей: перечень, заведение, правка.
 *
 * @param {object} currentUser — текущий пользователь (GET /api/me); нужен,
 *        чтобы отметить его собственную строку
 * @param {number} grantsVersion — счётчик правок матрицы выдачи; меняется —
 *        перечень перечитывается
 *
 * ПОЧЕМУ ПЕРЕЧЕНЬ ПЕРЕЧИТЫВАЕТСЯ ПОСЛЕ ПРАВКИ МАТРИЦЫ. Столбец «доступные
 * разделы» вычислен сервером из роли через матрицу выдачи. Правка матрицы
 * меняет его, ничего не меняя в самих записях, — и экран, оставшийся на
 * прежнем ответе, показывал бы старую выдачу как действующую. Приёмка §6
 * п. 4 требует, чтобы доступ менялся без выкладки; здесь он обязан ещё и
 * БЫТЬ ВИДЕН изменившимся.
 */
function UsersBlock({ currentUser, grantsVersion }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  // Пароль, показанный один раз, — { username, password, kind }. Живёт
  // здесь, а не в форме: форма заведения закрывается после успеха, и
  // пароль исчез бы вместе с ней.
  const [shown, setShown] = useState(null);

  const load = useCallback(() => {
    return listUsers()
      .then((data) => {
        setUsers(Array.isArray(data.users) ? data.users : []);
        setError("");
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    // grantsVersion в зависимостях намеренно: правка матрицы меняет
    // вычисленные разделы записей, и перечень обязан это показать.
  }, [load, grantsVersion]);

  /**
   * Выполняет действие над записью и показывает причину отказа ТЕКСТОМ.
   *
   * @param {function} действие — возвращает Promise вызова API
   * @param {function} послеУспеха — что сделать при успехе (необязательно)
   *
   * Текст берётся из поля detail ответа (его подставляет клиент API).
   * Сервер отвечает названными причинами — «нельзя снять последнего
   * администратора», «нельзя отключить самого себя», — и подменить их на
   * «Ошибка 400» значило бы выбросить единственное объяснение
   * происходящего.
   */
  const действие = (действиеAPI, послеУспеха) => {
    setActionError("");
    return действиеAPI()
      .then((результат) => {
        if (послеУспеха) послеУспеха(результат);
        return load();
      })
      .catch((e) => setActionError(e.message));
  };

  if (error) {
    return (
      <section className="card card-wide">
        <h2>Учётные записи</h2>
        <div className="error">{error}</div>
      </section>
    );
  }

  if (users === null) {
    return (
      <section className="card card-wide">
        <h2>Учётные записи</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  return (
    <section className="card card-wide">
      <h2>Учётные записи</h2>

      {shown && <PasswordOnce shown={shown} onDismiss={() => setShown(null)} />}

      {creating ? (
        <CreateUserForm
          onCreate={(поля) =>
            действие(
              () => createUser(поля),
              () => {
                setShown({
                  username: поля.username,
                  password: поля.password,
                  kind: "created",
                });
                setCreating(false);
              },
            )
          }
          onCancel={() => setCreating(false)}
        />
      ) : (
        <div className="inline">
          <button
            type="button"
            disabled={shown !== null}
            onClick={() => setCreating(true)}
          >
            Завести пользователя
          </button>
          {shown !== null && (
            <span className="muted">
              доступно после того, как показанный пароль сохранён
            </span>
          )}
        </div>
      )}

      {actionError && <div className="error">{actionError}</div>}

      {users.length === 0 ? (
        <p className="muted">Учётных записей нет.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Логин</th>
              <th>ФИО</th>
              <th>Должность</th>
              <th>Отдел</th>
              <th>Роль</th>
              <th>Состояние</th>
              <th>Последний вход</th>
              <th>Доступные разделы</th>
              <th>Правка</th>
            </tr>
          </thead>
          <tbody>
            {users.map((row) => (
              // Фрагмент: правка стоит ОТДЕЛЬНОЙ строкой под записью.
              // Форма внутри ячейки раздувала бы столбец и ломала
              // выравнивание всей таблицы.
              <Fragment key={row.id}>
                <tr>
                  <td>{row.username}</td>
                  <td>{row.full_name || "—"}</td>
                  <td>{row.position || "—"}</td>
                  <td>{row.department || "—"}</td>
                  <td>{roleTitle(row.role)}</td>
                  <td className={row.is_active ? undefined : "warn"}>
                    {row.is_active ? "активна" : "отключена"}
                  </td>
                  <td>{formatLastLogin(row.last_login_at)}</td>
                  <td>
                    <SectionsCell row={row} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        setExpanded(expanded === row.id ? null : row.id)
                      }
                    >
                      {expanded === row.id ? "свернуть" : "править"}
                    </button>
                  </td>
                </tr>
                {expanded === row.id && (
                  <tr>
                    <td colSpan={9}>
                      <UserEditor
                        row={row}
                        isSelf={currentUser && currentUser.id === row.id}
                        blocked={shown !== null}
                        onPatch={(поля) =>
                          действие(() => updateUser(row.id, поля))
                        }
                        onPassword={(пароль) =>
                          действие(
                            () => setUserPassword(row.id, пароль),
                            () =>
                              setShown({
                                username: row.username,
                                password: пароль,
                                kind: "reset",
                              }),
                          )
                        }
                        onActive={(активна) =>
                          действие(() => setUserActive(row.id, активна))
                        }
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Блок матрицы «роль × раздел»: галочки выдачи и сохранение целиком.
 *
 * @param {function} onSaved — вызывается после успешного сохранения;
 *        поднимает счётчик, по которому перечень записей перечитывается
 *
 * Строки — роли из ответа сервера (все, включая пустые), столбцы — каталог
 * разделов. Роли берутся из ОТВЕТА, а не из перечня в коде: роль, которую
 * сервер уже знает, обязана появиться в матрице и без выкладки фронтенда.
 *
 * Строка admin отрисована и отмечена целиком, но нередактируема
 * (locked из контракта, техспека BL-2026-10-08-a315 §3.3). Показать её
 * нередактируемой, а не дать нажать и получить отказ, — требование
 * контракта: признак locked приходит именно для этого.
 *
 * СОХРАНЕНИЕ — ОДНИМ ДЕЙСТВИЕМ НА ВСЮ МАТРИЦУ, и это не упрощение, а
 * форма решения: «кому какие разделы открыты» администратор решает
 * целиком. Цена — опасность опустошения, разобранная в заголовке файла;
 * отсюда два предупреждения: постоянное у опустевшей роли и обязательное
 * подтверждение перед самим сохранением.
 */
function AccessMatrixBlock({ onSaved }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  // Черновик: код роли → массив кодов разделов. Отдельно от ответа сервера,
  // чтобы было видно, что правится, и чтобы «Отменить правку» возвращало
  // действующее положение, а не перезапрашивало его.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  // Подтверждение опустошения: первое нажатие «Сохранить» при пустой роли
  // НЕ сохраняет, а показывает, какие роли останутся без разделов.
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  /** Раскладывает ответ сервера в состояние и черновик. */
  const apply = useCallback((ответ) => {
    setData(ответ);
    const d = {};
    for (const строка of ответ.roles || [])
      d[строка.role] = строка.sections || [];
    setDraft(d);
    setConfirmEmpty(false);
  }, []);

  useEffect(() => {
    loadAccessSections()
      .then(apply)
      .catch((e) => setError(e.message));
  }, [apply]);

  if (error) {
    return (
      <section className="card card-wide">
        <h2>Выдача разделов: роль × раздел</h2>
        <div className="error">{error}</div>
      </section>
    );
  }

  if (data === null) {
    return (
      <section className="card card-wide">
        <h2>Выдача разделов: роль × раздел</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  const catalog = data.sections || [];
  const roles = data.roles || [];
  const payload = fullMatrixPayload(roles, draft, catalog);
  const пустые = rolesLeftEmpty(payload);

  /** Переключает галочку выдачи; locked-строки сюда не попадают. */
  const toggle = (role, code) => {
    setNotice("");
    setConfirmEmpty(false);
    const текущие = draft[role] || [];
    setDraft({
      ...draft,
      [role]: текущие.includes(code)
        ? текущие.filter((c) => c !== code)
        : [...текущие, code],
    });
  };

  /**
   * Сохраняет матрицу целиком.
   *
   * Первое нажатие при пустой роли НЕ сохраняет: оно переводит блок в
   * состояние подтверждения, и предупреждение звучит ДО запроса (приёмка
   * §6 п. 6). Второе нажатие — уже сохранение.
   *
   * Побочные эффекты: PUT /api/access/sections (замена выдачи целиком),
   * перечитывание перечня записей через onSaved.
   */
  const save = () => {
    if (пустые.length > 0 && !confirmEmpty) {
      setConfirmEmpty(true);
      setSaveError("");
      setNotice("");
      return;
    }
    setSaving(true);
    setSaveError("");
    saveAccessSections(payload)
      .then((ответ) => {
        apply(ответ);
        setNotice("Выдача сохранена и действует сразу, без выкладки.");
        if (onSaved) onSaved();
      })
      .catch((e) => setSaveError(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <section className="card card-wide">
      <h2>Выдача разделов: роль × раздел</h2>

      <p className="block-legend">
        Галочка означает, что роль ВИДИТ раздел. Право менять данные внутри
        раздела галочкой не выдаётся: его задают предикаты операций в коде, и
        наблюдатель видит сводку, ничего в ней не правя. Выдача роли
        «Администратор» неотзываема — её право на все разделы зашито в коде,
        иначе администратор, снявший себе последний раздел, терял бы и доступ к
        этому экрану.
      </p>

      <table className="access-matrix">
        <thead>
          <tr>
            <th>Роль</th>
            {catalog.map((s) => (
              <th key={s.code}>{s.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => {
            const выдано = r.locked ? r.sections || [] : draft[r.role] || [];
            const опустела = !r.locked && выдано.length === 0;
            return (
              <tr key={r.role} className={опустела ? "row-empty" : undefined}>
                <th scope="row">
                  {roleTitle(r.role)}
                  {r.locked && (
                    <span
                      className="badge"
                      title="Выдача роли admin не хранится и не правится: её право на все разделы зашито в коде и неотзываемо."
                    >
                      неотзываемо
                    </span>
                  )}
                  {опустела && (
                    <span className="badge badge-warn">без разделов</span>
                  )}
                </th>
                {catalog.map((s) => (
                  <td key={s.code}>
                    <input
                      type="checkbox"
                      checked={выдано.includes(s.code)}
                      disabled={r.locked}
                      aria-label={`${roleTitle(r.role)} — ${s.title}`}
                      onChange={() => toggle(r.role, s.code)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {пустые.length > 0 && (
        <div className="incomplete">
          <strong>
            Роли останутся без единого раздела:{" "}
            {пустые.map((role) => roleTitle(role)).join(", ")}.
          </strong>
          <p className="muted">
            Войти такой ролью можно, но вместо разделов человек увидит экран
            «Разделы не выданы, обратитесь к администратору». Если это и
            задумано — сохраняйте; если нет — поставьте галочки до сохранения.
          </p>
        </div>
      )}

      {confirmEmpty && (
        <div className="notice">
          Сохранение заменит выдачу целиком. Подтвердите, что перечисленные выше
          роли остаются без разделов, — или отмените и поправьте галочки.
        </div>
      )}

      {saveError && <div className="error">{saveError}</div>}
      {notice && <p className="muted">{notice}</p>}

      <div className="inline">
        <button type="button" disabled={saving} onClick={save}>
          {saving
            ? "Сохраняем…"
            : confirmEmpty
              ? "Сохранить всё равно"
              : "Сохранить выдачу"}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            apply(data);
            setSaveError("");
            setNotice("");
          }}
        >
          Отменить правку
        </button>
      </div>

      <p className="muted">
        Сохранение отправляет ПОЛНУЮ матрицу по всем правимым ролям, а не
        изменённые строки: запрос заменяет выдачу целиком, и роль, не попавшая в
        тело, потеряла бы все разделы. Так однажды и произошло — выдача трёх
        ролей обнулилась, и заметно это стало только по следующему запросу.
      </p>
    </section>
  );
}

/**
 * Раздел «Настройки» целиком: записи сверху, матрица выдачи под ними.
 *
 * @param {object} user — текущий пользователь (GET /api/me)
 *
 * Порядок блоков отвечает порядку вопросов: сначала «кто есть в системе и
 * что ему доступно», затем «чем это доступное задано». Обратный порядок
 * заставлял бы листать к матрице, чтобы понять строку таблицы.
 *
 * Счётчик grants связывает блоки в одну сторону: матрица сохранена →
 * перечень записей перечитан, потому что столбец «доступные разделы»
 * вычислен из матрицы. Обратной связи нет: смена роли одной записи матрицу
 * не меняет.
 */
export default function SettingsSection({ user }) {
  const [grants, setGrants] = useState(0);

  return (
    <>
      <UsersBlock currentUser={user} grantsVersion={grants} />
      <AccessMatrixBlock onSaved={() => setGrants((v) => v + 1)} />
    </>
  );
}
